/**
 * Stationing on the project map (7.2): station ticks and labels along every project alignment, and the pointer's station
 * readout. ArcGIS-free so karma can run it; the map layer (stationing-map-layer.ts) only draws what this returns. The
 * maths is the shared alignment-stationing and the place dialog's zoom-aware station marks (drawing-place-stations):
 * the same ticks and ladder as Place on map → By stations, so the two read as one thing.
 */
import { FT, pointAt, ProjectAlignment, StationAt, stationAt, stationName, stationRange } from './alignment-stationing';
import {
  stationIntervals, stationLabelStations, stationTicks,
} from '../../modules/projects/pages/projects-details-page/components/tab-screens/drawings/drawing-place-stations';

/** The readout shows only this close to an alignment (feet): further away a station would mislead */
export const FAR_FROM_ALIGNMENT_FEET = 300;
/** Within this (feet) of the line the readout says CL rather than a side */
export const CENTERLINE_FEET = 1;
/** Half a tick's length on screen (px), as the place dialog's */
export const STATIONING_TICK_HALF_PX = 6;
/** The station label's gap past the tick's end (px) */
export const STATIONING_LABEL_GAP_PX = 4;

export interface LonLat {
  lat: number;
  lon: number;
}

/** A lon / lat box (the map's extent), to leave out what is off screen */
export interface LonLatBox {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

/**
 * What the map shows at a zoom (`metresPerPx`: ground metres per screen pixel), on the place dialog's ladder:
 *  - close in: every 100 ft labelled;
 *  - middle: labels every 500 ft (ticks every 100 ft);
 *  - further out: labels every 1000 ft;
 *  - furthest out: only each alignment's name along its line.
 * The names show from the middle out, so parallel alignments (S25C / N25C) can be told apart.
 */
export function stationingDensity(metresPerPx: number): { labelEvery: number | null; tickEvery: number | null; names: boolean } {
  const { labelEvery, tickEvery } = stationIntervals(metresPerPx);
  if (!labelEvery || labelEvery > 1000) {
    return { labelEvery: null, tickEvery: null, names: true };
  }
  return { labelEvery, tickEvery, names: labelEvery > 100 };
}

export interface StationingTick {
  alignment: number;
  station: number;
  major: boolean;
  left: LonLat;
  right: LonLat;
}

export interface StationingLabel {
  alignment: number;
  station: number;
  text: string;
  /** A fixed gap past the tick's end on the right-hand side of the stationing (one side, as on mobile) */
  at: LonLat;
  /** Which way the text runs from `at`: away from the line (left-aligned when that side lies east) */
  align: 'left' | 'right';
}

export interface StationingName {
  alignment: number;
  text: string;
  at: LonLat;
  /** Screen degrees clockwise from east, turned to read upright (-90 to 90) */
  angle: number;
}

export interface StationingMarks {
  ticks: StationingTick[];
  labels: StationingLabel[];
  names: StationingName[];
}

const inBox = (p: LonLat, box: LonLatBox | null | undefined) =>
  !box || (p.lon >= box.xmin && p.lon <= box.xmax && p.lat >= box.ymin && p.lat <= box.ymax);

/** A label chip's width on screen (px): Montserrat bold at 9-10 px, and the chip's padding */
const LABEL_CHAR_PX = 6.5;
const CHIP_PAD_PX = 3;
export function labelWidthPx(text: string): number {
  return text.length * LABEL_CHAR_PX + CHIP_PAD_PX * 2;
}

/**
 * Every alignment's ticks, labels and names at this zoom, culled to `box` (S25C alone is 13,100 ft). Major ticks and
 * labels fall on round stations (multiples of the label step) even when the alignment starts between them; the ends
 * are labelled too, as in the place dialog.
 */
export function stationingMarks(alignments: ProjectAlignment[], metresPerPx: number, box?: LonLatBox | null): StationingMarks {
  const { labelEvery, tickEvery, names } = stationingDensity(metresPerPx);
  const halfFt = (STATIONING_TICK_HALF_PX * (metresPerPx || 1)) / FT;
  const labelGapFt = (STATIONING_LABEL_GAP_PX * (metresPerPx || 1)) / FT;
  const marks: StationingMarks = { ticks: [], labels: [], names: [] };
  alignments.forEach((alignment, index) => {
    if (tickEvery) {
      for (const t of stationTicks(alignment, tickEvery, halfFt)) {
        if (inBox(t.at, box)) {
          marks.ticks.push({ alignment: index, station: t.station, major: !!labelEvery && t.station % labelEvery === 0, left: t.left, right: t.right });
        }
      }
    }
    if (labelEvery) {
      const { first, last } = stationRange(alignment.alignment);
      for (const station of stationLabelStations(first, last, labelEvery, metresPerPx)) {
        // One side of the line (the right-hand side of the stationing), a fixed gap past the tick; the text runs away
        // from the line, so it never sits across it
        const on = pointAt(alignment.alignment, station);
        const at = pointAt(alignment.alignment, station, halfFt + labelGapFt, 'R');
        if (on && at && inBox(at, box)) {
          marks.labels.push({ alignment: index, station, text: stationName(station), at, align: at.lon >= on.lon ? 'left' : 'right' });
        }
      }
    }
    if (names && alignment.name) {
      const name = alignmentNameMark(alignment, index, box, metresPerPx);
      if (name) marks.names.push(name);
    }
  });
  return marks;
}

/** How many times the name's width the line must run on screen before it is named: a chip barely shorter than the
 *  line covers it end to end (Fort Bend at project zoom) */
const NAME_ROOM = 3;

/**
 * The name at the middle of the alignment's part on screen, along the line there; none when that part is shorter on
 * screen than NAME_ROOM names (zoomed out, a 4 km line is a few pixels and its chip would cover the city)
 */
function alignmentNameMark(
  alignment: ProjectAlignment, index: number, box: LonLatBox | null | undefined, metresPerPx: number,
): StationingName | null {
  const shown = alignment.alignment.coords.filter(([lon, lat]) => inBox({ lon, lat }, box));
  if (!shown.length) return null;
  const stations = shown.map(([lon, lat]) => stationAt(alignment.alignment, { lon, lat }).station);
  const shownPx = ((Math.max(...stations) - Math.min(...stations)) * FT) / (metresPerPx || 1);
  if (shownPx < labelWidthPx(alignment.name) * NAME_ROOM) return null;
  const middle = (Math.min(...stations) + Math.max(...stations)) / 2;
  const { first, last } = stationRange(alignment.alignment);
  const a = pointAt(alignment.alignment, Math.max(first, middle - 50));
  const b = pointAt(alignment.alignment, Math.min(last, middle + 50));
  const at = pointAt(alignment.alignment, middle);
  if (!a || !b || !at) return null;
  return { alignment: index, text: alignment.name, at, angle: uprightAngle(a, b) };
}

/** The screen angle of the line from `a` to `b` (degrees clockwise from east), turned so text along it reads upright */
export function uprightAngle(a: LonLat, b: LonLat): number {
  const east = (b.lon - a.lon) * Math.cos((((a.lat + b.lat) / 2) * Math.PI) / 180);
  const north = b.lat - a.lat;
  let angle = (-Math.atan2(north, east) * 180) / Math.PI;
  if (angle > 90) angle -= 180;
  if (angle <= -90) angle += 180;
  return angle;
}

/** A label's box on screen (px) */
export interface ScreenBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * The labels that fit: in order (every alignment's together, names first), each kept only if it overlaps none kept
 * before it and `blocked` (the map's floating panels and buttons) does not cover it, so labels never overlap.
 */
export function deconflict(boxes: ScreenBox[], gapPx = 2, blocked: (box: ScreenBox) => boolean = () => false): boolean[] {
  const kept: ScreenBox[] = [];
  return boxes.map(b => {
    const clash = kept.some(k => b.x < k.x + k.width + gapPx && k.x < b.x + b.width + gapPx && b.y < k.y + k.height + gapPx && k.y < b.y + b.height + gapPx);
    if (clash || blocked(b)) return false;
    kept.push(b);
    return true;
  });
}

/**
 * The alignment nearest the point and its station there, within FAR_FROM_ALIGNMENT_FEET; null further away, or past
 * either end of it (a station there would be the end's, which is misleading).
 */
export function nearestAlignment(alignments: ProjectAlignment[], point: LonLat): { index: number; at: StationAt } | null {
  const best = closestAlignment(alignments, point);
  return !best || best.at.offset_ft > FAR_FROM_ALIGNMENT_FEET || best.at.beyond ? null : best;
}

/** The alignment closest to the point, however far (past an end, the distance is to that end) */
export function closestAlignment(alignments: ProjectAlignment[], point: LonLat): { index: number; at: StationAt } | null {
  let best: { index: number; at: StationAt } | null = null;
  for (let index = 0; index < alignments.length; index += 1) {
    const at = stationAt(alignments[index].alignment, point);
    if (!best || at.offset_ft < best.at.offset_ft) best = { index, at };
  }
  return best;
}

/** "STA 405+40 · 12' LT": the station and offset to the foot, CL within a foot of the line (spec §3c: no name) */
export function stationReadoutText(at: StationAt): string {
  const where = at.offset_ft < CENTERLINE_FEET || !at.side ? 'CL' : `${Math.round(at.offset_ft)}' ${at.side === 'L' ? 'LT' : 'RT'}`;
  return `STA ${stationName(Math.round(at.station))} · ${where}`;
}

/**
 * Spec §3c: the one active alignment. Locked to a name (while the project still has it), else Automatic: the nearest to
 * the point (the click or the pointer), else the last automatic choice, else the first. -1 without alignments.
 */
export function activeAlignmentIndex(alignments: ProjectAlignment[], locked: string | null, point?: LonLat | null, lastAuto = -1): number {
  if (!alignments.length) return -1;
  const lockedIndex = locked ? alignments.findIndex(a => a.name === locked) : -1;
  if (lockedIndex >= 0) return lockedIndex;
  if (point) return closestAlignment(alignments, point)?.index ?? 0;
  return lastAuto >= 0 && lastAuto < alignments.length ? lastAuto : 0;
}

/** §3c: how an alignment draws: the active one bold, the others faded; a lone alignment is drawn plain */
export function alignmentEmphasis(index: number, active: number, count: number): 'active' | 'faded' | 'plain' {
  if (count < 2) return 'plain';
  return index === active ? 'active' : 'faded';
}

/**
 * §3c: Automatic's next active alignment. A click (or a record's popup opening) takes the nearest to that point; the
 * pointer takes the nearest to it only while no callout or popup station line is open (`held`), so the line, the
 * highlight and the labels never jump while the user reaches for Copy or ✕.
 */
export function nextAutoIndex(current: number, nearest: number, source: 'click' | 'pointer', held: boolean): number {
  if (nearest < 0) return current;
  return source === 'pointer' && held ? current : nearest;
}

/** §3c: only the active alignment is labelled (stations and its name); the others are a faded line and ticks */
export function alignmentLabelled(index: number, active: number, count: number): boolean {
  return alignmentEmphasis(index, active, count) !== 'faded';
}

/** §3c: the alignment chip shows only with two or more alignments, while Stationing is on */
export function showAlignmentChip(alignmentCount: number, stationingOn: boolean): boolean {
  return stationingOn && alignmentCount > 1;
}

/** The pointer's readout on the active alignment, or null when it is more than 300 ft away or past an end */
export function pointerStationReadout(alignments: ProjectAlignment[], point: LonLat, locked: string | null = null): string | null {
  const index = activeAlignmentIndex(alignments, locked, point);
  if (index < 0) return null;
  const at = stationAt(alignments[index].alignment, point);
  return at.offset_ft > FAR_FROM_ALIGNMENT_FEET || at.beyond ? null : stationReadoutText(at);
}

/** A clicked point's callout (spec §3c): its one line and what Copy puts on the clipboard (null: nothing to copy) */
export interface StationPick {
  lines: string[];
  copy: string | null;
  /** The alignment it was read on */
  index: number;
}

/**
 * What a click reads on the active alignment (spec §3c), one short line with the readout's maths and rounding:
 * `STA 29+47 · 268' LT`; more than 300 ft away, `412 ft from S25C`; past an end, `Beyond the end of S25C (531+00)`.
 * Null without alignments.
 */
export function stationPick(alignments: ProjectAlignment[], point: LonLat, locked: string | null = null): StationPick | null {
  const index = activeAlignmentIndex(alignments, locked, point);
  if (index < 0) return null;
  const alignment = alignments[index];
  const name = alignment.name || 'the alignment';
  const at = stationAt(alignment.alignment, point);
  if (at.offset_ft > FAR_FROM_ALIGNMENT_FEET) {
    return { lines: [`${Math.round(at.offset_ft).toLocaleString('en-US')} ft from ${name}`], copy: null, index };
  }
  if (at.beyond) {
    const { first, last } = stationRange(alignment.alignment);
    const end = at.beyond === 'start' ? first : last;
    return { lines: [`Beyond the ${at.beyond === 'start' ? 'start' : 'end'} of ${name} (${stationName(Math.round(end))})`], copy: null, index };
  }
  const line = stationReadoutText(at);
  return { lines: [line], copy: line, index };
}

/** A hit-test result, as far as the click needs it */
export interface MapHit {
  type?: string;
  graphic?: { layer?: unknown; attributes?: { isSiteGeometry?: boolean } | null } | null;
}

/**
 * The click landed on empty map: nothing under it but the basemap, a site's boundary (the background the whole site
 * sits in, often around the whole alignment) and the Stationing layers. A record, pin, cluster or design feature keeps
 * its own popup.
 */
export function isEmptyMapClick(hits: MapHit[], own: unknown[]): boolean {
  return !hits.some(hit => isForegroundHit(hit, own));
}

/** A hit on something with its own click (record, pin, cluster, design feature): not a site's boundary, not Stationing */
export function isForegroundHit(hit: MapHit, own: unknown[]): boolean {
  return hit.type === 'graphic' && !!hit.graphic?.layer && !own.includes(hit.graphic.layer) && !hit.graphic.attributes?.isSiteGeometry;
}

/** What a click's design-layer inspection found */
export type ReferenceClickResult = 'none' | 'candidates' | 'no-candidates' | 'viewing-only' | 'error';

/**
 * Spec §3a: one owner per click. Design features within the inspection tolerance keep their card (it carries the
 * station line) and no station callout draws; otherwise, with Stationing answering clicks, the callout does instead of
 * an empty "No reference features" or "viewing only" panel. A failed inspection keeps its retry message.
 */
export function referencePanelWins(result: ReferenceClickResult, stationingAnswers: boolean): boolean {
  if (result === 'candidates' || result === 'error') return true;
  if (result === 'none') return false;
  return !stationingAnswers;
}

/**
 * Spec §3a / §3c: the one line a record's or feature's popup gains for the clicked point (not its centroid), on the
 * active alignment; null without alignments.
 */
export function stationPickLine(alignments: ProjectAlignment[], point: LonLat, locked: string | null = null): string | null {
  return stationPick(alignments, point, locked)?.lines[0] ?? null;
}

/** The gap between the dot and the callout (px), which its pointer spans */
export const CALLOUT_GAP_PX = 12;
const CALLOUT_MARGIN_PX = 8;

/**
 * Where the click callout goes (spec §3b): centred above the dot, or below it when it would leave the top of the map;
 * never over the dot; slid sideways to stay on screen, its pointer still on the dot
 */
export function calloutPlacement(
  dot: { x: number; y: number }, size: { width: number; height: number }, view: { width: number; height: number },
): { left: number; top: number; below: boolean; pointerLeft: number } {
  const below = dot.y - CALLOUT_GAP_PX - size.height < CALLOUT_MARGIN_PX;
  const top = below ? dot.y + CALLOUT_GAP_PX : dot.y - CALLOUT_GAP_PX - size.height;
  const maxLeft = Math.max(CALLOUT_MARGIN_PX, view.width - size.width - CALLOUT_MARGIN_PX);
  const left = Math.min(maxLeft, Math.max(CALLOUT_MARGIN_PX, dot.x - size.width / 2));
  const pointerLeft = Math.min(size.width - 10, Math.max(10, dot.x - left));
  return { left, top, below, pointerLeft };
}
